import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { OnboardingStatus } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCanWithoutBuilding } from "@/lib/policy";
import { OnboardingTable } from "./-components/onboarding-table";
import { STALLED } from "@/routes/_authenticated/_workforce/hr/-components/status-cell";

export const Route = createFileRoute("/_authenticated/_workforce/hr/onboarding")({
  component: HrOnboarding,
});

/** Where each new hire has got to between being hired and being ready to work. Only the training
 * step moves on its own; a manager moves the rest, because only a manager knows whether the
 * evaluation actually happened. */
function HrOnboarding() {
  const { facilityId } = useFacility();
  // A step is moved on the person, which names no building.
  const canManage = useCanWithoutBuilding("hr:manage");
  const queryClient = useQueryClient();
  const [includeComplete, setIncludeComplete] = useState(false);

  const board = useQuery(
    orpc.administration.staff.onboarding.list.queryOptions({
      input: { facilityId, includeComplete },
    }),
  );

  const setStatus = useMutation(
    orpc.administration.staff.onboarding.setStatus.mutationOptions({
      onSuccess: async () => {
        toast.success("Onboarding updated");
        await queryClient.invalidateQueries({
          queryKey: orpc.administration.staff.onboarding.key(),
        });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = board.data ?? [];
  const stalled = rows.filter((row) =>
    STALLED.includes(row.onboardingStatus as OnboardingStatus),
  ).length;

  return (
    <>
      <PageHeader
        title="Onboarding"
        count={board.data ? rows.length : undefined}
        meta={
          board.isPending
            ? "Loading…"
            : rows.length === 0
              ? "Nobody is being onboarded"
              : `In progress${stalled > 0 ? `, ${stalled} stalled` : ""}`
        }
        actions={
          <Button variant="outline" onClick={() => setIncludeComplete((shown) => !shown)}>
            {includeComplete ? "Hide finished" : "Show finished"}
          </Button>
        }
      />

      {board.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : board.isError ? (
        <ReadFailed
          message="Could not load the board."
          error={board.error}
          onRetry={() => void board.refetch()}
          retrying={board.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nobody is being onboarded"
          description="People appear here when a candidate is converted into an employee."
        />
      ) : (
        <OnboardingTable
          rows={rows}
          canManage={canManage}
          onSetStatus={(userId, onboardingStatus) => setStatus.mutate({ userId, onboardingStatus })}
        />
      )}
    </>
  );
}
